import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Content Studio supports a single active profile per browser session, and that session is shared
 * across every tab of the same origin (server session cookie + `User` in localStorage). When the
 * user opens "Review Now" for a different profile, the new tab bootstraps a fresh session and the
 * older tab silently becomes stale — its CTAs would otherwise fail with a confusing profile-mismatch
 * error.
 *
 * This service identifies the tab whose profile is currently active, detects when a newer tab takes
 * over with a DIFFERENT profile, and routes the stale tab to the full-page "Newer Session Detected"
 * screen (/newer-session). Tabs on the SAME profile share the id and are never flagged.
 *
 * Two detection layers:
 *  - Proactive: a BroadcastChannel/localStorage signal routes to the screen the instant a newer tab
 *    takes over.
 *  - Reactive: the backend rejects stale requests with 409 (handled in the HTTP interceptor), which
 *    also routes here — a backstop for when the proactive signal is missed.
 */
@Injectable({ providedIn: 'root' })
export class SessionInstanceService {
  /** Per-tab id — sessionStorage is NOT shared between tabs, so each tab keeps its own. */
  private static readonly TAB_ID_KEY = 'cs.tabSessionId';
  /** Shared across tabs — the id of the currently active profile/session. */
  private static readonly ACTIVE_ID_KEY = 'cs.activeSessionId';
  private static readonly CHANNEL_NAME = 'cs-active-session';

  private channel: BroadcastChannel | null = null;
  /** Ensures we route to the screen at most once per tab. */
  private prompted = false;

  constructor(private router: Router, private zone: NgZone) {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(SessionInstanceService.CHANNEL_NAME);
      this.channel.onmessage = (event) => this.onActiveSessionChanged(event.data);
    }
    // Fallback / cross-app: localStorage `storage` events fire in OTHER tabs of the same origin.
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key === SessionInstanceService.ACTIVE_ID_KEY) {
          this.onActiveSessionChanged(event.newValue);
        }
      });
    }
  }

  /**
   * Marks THIS tab as belonging to the active session. Called after a successful session bootstrap
   * (InitializeUserSession / ApplyProfile) with the id the server returned. The id is the active
   * profile, so opening the same profile in another tab keeps the same value here.
   */
  adoptActiveSession(activeSessionId: string | null | undefined): void {
    if (!activeSessionId) {
      return;
    }
    sessionStorage.setItem(SessionInstanceService.TAB_ID_KEY, activeSessionId);
    localStorage.setItem(SessionInstanceService.ACTIVE_ID_KEY, activeSessionId);
    // This tab now matches the active session, so it is no longer stale.
    this.prompted = false;
    this.channel?.postMessage(activeSessionId);
  }

  /** The session id this tab bootstrapped with — echoed to the backend on every request. */
  getTabSessionId(): string {
    return sessionStorage.getItem(SessionInstanceService.TAB_ID_KEY) || '';
  }

  /** Routes this (stale) tab to the full-page "Newer Session Detected" screen, once. */
  markStaleAndPrompt(): void {
    if (this.prompted) {
      return;
    }
    const myId = this.getTabSessionId();
    // Not armed yet: this tab never bootstrapped a session, so it cannot be "stale".
    if (!myId) {
      return;
    }
    this.prompted = true;
    // Navigate inside NgZone: this can be triggered from a BroadcastChannel / storage event that
    // fires outside Angular's zone, where router navigation would not update the view.
    this.zone.run(() => this.router.navigate(['/newer-session']));
  }

  private onActiveSessionChanged(incomingActiveId: string | null): void {
    const myId = this.getTabSessionId();
    if (!incomingActiveId || !myId) {
      return;
    }
    if (incomingActiveId !== myId) {
      this.markStaleAndPrompt();
    }
  }
}
